/**
 * Graphic plate prompts to use again, each shown with the plate it made.
 *
 *   PromptLibrary   — "Favourite & recent prompts": the user's favourites (by
 *                     label, with each plate's picture), then the latest 10
 *                     prompts anyone made a plate with. "Use" puts a prompt in
 *                     "Create graphic plate".
 *   FavoriteButton  — in a plate's viewer: keep its prompt as a favourite, with a label.
 *
 * Favourites are each user's own; recent prompts are everyone's.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiStar, FiX, FiLoader, FiSearch, FiEdit2, FiTrash2, FiCheck, FiImage, FiPaperclip, FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { showError } from '../FullStory/shared';

const LABEL_MAX = 40;

/** The user's favourites and labels, and the recent prompts: { data, loading, reload }. */
export function usePlatePrompts(active = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await api.platePrompts());
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (active) reload(); }, [active, reload]);
  return { data, loading, error, reload };
}

/** A loading error, said where it happened, with "Try again". */
const LoadError = ({ error, onRetry }) => {
  const t = useT();
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-center justify-between gap-2">
      <span>{t('platePrompts.loadFailed', { error })}</span>
      <button type="button" onClick={onRetry} className="underline whitespace-nowrap">{t('plateTemplates.tryAgain')}</button>
    </div>
  );
};

/** A date as the cards show it: 2 Nov 2026. */
const day = (at) => (at ? new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** A label to type, with the user's labels to pick from. */
const LabelInput = ({ value, onChange, labels, autoFocus = false, onEnter }) => {
  const t = useT();
  return (
    <div className="space-y-1.5">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, LABEL_MAX))}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter?.(); } }}
        maxLength={LABEL_MAX}
        placeholder={t('platePrompts.labelPlaceholder')}
        aria-label={t('platePrompts.label')}
        autoFocus={autoFocus}
        className="input text-sm"
      />
      {labels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {labels.map((l) => (
            <button
              key={l.label}
              type="button"
              onClick={() => onChange(l.label)}
              className={`px-2 py-0.5 rounded-full border text-[11px] ${value === l.label ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** A prompt's text: a few lines, or all of it. */
const PromptText = ({ text }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const long = text.length > 220;
  return (
    <div>
      <p className={`text-xs text-gray-700 whitespace-pre-wrap break-words ${open ? '' : 'line-clamp-3'}`}>{text}</p>
      {long && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-primary-600 hover:underline">
          {open ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />} {open ? t('platePrompts.less') : t('platePrompts.more')}
        </button>
      )}
    </div>
  );
};

/** A prompt as a card: the plate it made, its text, and what can be done with it. */
const PromptCard = ({ image, title, badge, meta, text, references, children }) => {
  const t = useT();
  return (
    <li className="rounded-xl border border-gray-200 overflow-hidden bg-white flex flex-col">
      <div className="relative aspect-video bg-gray-100">
        {image ? <img src={image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" /> : (
          <span className="absolute inset-0 flex items-center justify-center text-gray-300"><FiImage size={28} /></span>
        )}
        {badge && <span className="absolute top-2 left-2 max-w-[80%] truncate rounded-full bg-black/70 text-white text-[11px] px-2 py-0.5">{badge}</span>}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900 truncate" title={title}>{title}</p>
          {meta && <p className="text-[11px] text-gray-500 truncate">{meta}</p>}
        </div>
        <PromptText text={text} />
        {references > 0 && (
          <p className="text-[11px] text-amber-700 inline-flex items-center gap-1" title={t('platePrompts.referencesHint')}>
            <FiPaperclip size={11} /> {t('platePrompts.references', { count: references })}
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">{children}</div>
      </div>
    </li>
  );
};

/**
 * "Favourite & recent prompts": the user's favourites, filtered by label or a
 * search, then the latest prompts anyone made a plate with. onUse(text) puts
 * a prompt to use.
 */
export const PromptLibrary = ({ onUse, onClose }) => {
  const t = useT();
  const { data, loading, error, reload } = usePlatePrompts(true);
  const [label, setLabel] = useState('');
  const [search, setSearch] = useState('');
  // A favourite being relabelled ({ id, label }), or a recent prompt being marked ({ plateId, label }).
  const [editing, setEditing] = useState(null);
  const [marking, setMarking] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const favorites = data?.favorites || [];
  const labels = data?.labels || [];
  const favoriteOf = useMemo(() => new Map(favorites.map((f) => [f.plateId, f])), [favorites]);
  // A label that is gone (its last favourite removed or relabelled): back to all.
  useEffect(() => { if (label && !labels.some((l) => l.label === label)) setLabel(''); }, [label, labels]);
  const needle = search.trim().toLowerCase();
  const shown = favorites.filter((f) => (!label || f.label === label)
    && (!needle || [f.label, f.text, f.plateName].some((x) => String(x || '').toLowerCase().includes(needle))));

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await reload();
    } catch (e) {
      showError(e);
    } finally {
      setBusy('');
    }
  };
  const relabel = () => run(`edit-${editing.id}`, async () => { await api.relabelPlatePromptFavorite(editing.id, editing.label); setEditing(null); });
  const remove = (f) => {
    if (!window.confirm(t('platePrompts.removeConfirm', { label: f.label }))) return;
    run(`remove-${f.id}`, () => api.removePlatePromptFavorite(f.id));
  };
  const mark = () => run(`mark-${marking.plateId}`, async () => { await api.addPlatePromptFavorite(marking.plateId, marking.label); setMarking(null); });

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3 sm:p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label={t('platePrompts.title')}>
      <div className="w-full max-w-5xl max-h-[94vh] flex flex-col bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FiStar size={18} className="text-amber-500" /> {t('platePrompts.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('platePrompts.hint')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm"><FiX size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {!data && error && <LoadError error={error} onRetry={reload} />}
          {!data && loading && (
            <p className="text-sm text-gray-500 flex items-center gap-2"><FiLoader size={14} className="animate-spin" /> {t('platePrompts.loading')}</p>
          )}

          {data && (
            <section aria-label={t('platePrompts.favorites')} className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><FiStar size={14} className="text-amber-500 fill-current" /> {t('platePrompts.favorites')}</h3>
                <span className="text-xs text-gray-400">{favorites.length}</span>
                {favorites.length > 0 && (
                  <div className="relative ml-auto w-full sm:w-64">
                    <FiSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('platePrompts.search')} aria-label={t('platePrompts.search')} style={{ paddingLeft: '2rem' }} className="input text-sm py-1.5" />
                  </div>
                )}
              </div>
              {labels.length > 0 && (
                <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('platePrompts.labels')}>
                  <button type="button" role="tab" aria-selected={!label} onClick={() => setLabel('')} className={`px-2.5 py-1 rounded-full border text-xs ${!label ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {t('platePrompts.allLabels')} <span className="opacity-70">{favorites.length}</span>
                  </button>
                  {labels.map((l) => (
                    <button key={l.label} type="button" role="tab" aria-selected={label === l.label} onClick={() => setLabel(l.label)} className={`px-2.5 py-1 rounded-full border text-xs ${label === l.label ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-200 text-gray-700 hover:border-amber-400'}`}>
                      {l.label} <span className="opacity-70">{l.count}</span>
                    </button>
                  ))}
                </div>
              )}
              {!favorites.length && <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-200 px-4 py-3">{t('platePrompts.noFavorites')}</p>}
              {favorites.length > 0 && !shown.length && <p className="text-sm text-gray-400">{t('platePrompts.noMatch')}</p>}
              {shown.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {shown.map((f) => (
                    <PromptCard
                      key={f.id}
                      image={f.hasImage ? api.platePromptImageUrl(f.id) : null}
                      badge={f.label}
                      title={f.plateName || f.label}
                      meta={t('platePrompts.savedOn', { date: day(f.createdAt) })}
                      text={f.text}
                      references={f.references}
                    >
                      {editing?.id === f.id ? (
                        <div className="w-full space-y-2">
                          <LabelInput value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} labels={labels} autoFocus onEnter={relabel} />
                          <div className="flex justify-end gap-1.5">
                            <button type="button" onClick={() => setEditing(null)} className="btn btn-ghost btn-sm">{t('plateTemplates.cancel')}</button>
                            <button type="button" onClick={relabel} disabled={!editing.label.trim() || busy === `edit-${f.id}`} className="btn btn-primary btn-sm flex items-center gap-1">
                              {busy === `edit-${f.id}` ? <FiLoader size={13} className="animate-spin" /> : <FiCheck size={13} />} {t('platePrompts.saveLabel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button type="button" onClick={() => onUse(f.text)} className="btn btn-primary btn-sm">{t('platePrompts.use')}</button>
                          <button type="button" onClick={() => setEditing({ id: f.id, label: f.label })} title={t('platePrompts.relabel')} aria-label={t('platePrompts.relabel')} className="btn btn-ghost btn-sm px-2"><FiEdit2 size={13} /></button>
                          <button type="button" onClick={() => remove(f)} disabled={busy === `remove-${f.id}`} title={t('platePrompts.remove')} aria-label={t('platePrompts.remove')} className="btn btn-ghost btn-sm px-2 text-gray-500 hover:text-red-600">
                            {busy === `remove-${f.id}` ? <FiLoader size={13} className="animate-spin" /> : <FiTrash2 size={13} />}
                          </button>
                        </>
                      )}
                    </PromptCard>
                  ))}
                </ul>
              )}
            </section>
          )}

          {data && (
            <section aria-label={t('platePrompts.recent')} className="space-y-3">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><FiClock size={14} className="text-gray-500" /> {t('platePrompts.recent')}</h3>
                <span className="text-xs text-gray-500">{t('platePrompts.recentHint')}</span>
              </div>
              {!data.recent.length && <p className="text-sm text-gray-500">{t('platePrompts.noRecent')}</p>}
              {data.recent.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.recent.map((r) => {
                    const fav = favoriteOf.get(r.plateId);
                    return (
                      <PromptCard
                        key={r.plateId}
                        image={api.plateTemplateUrl(r.plateId, 'v1.jpg', r.createdAt)}
                        badge={fav ? `★ ${fav.label}` : null}
                        title={r.name}
                        meta={[r.createdByName, day(r.createdAt)].filter(Boolean).join(' · ')}
                        text={r.text}
                        references={r.references}
                      >
                        {marking?.plateId === r.plateId ? (
                          <div className="w-full space-y-2">
                            <LabelInput value={marking.label} onChange={(v) => setMarking({ ...marking, label: v })} labels={labels} autoFocus onEnter={mark} />
                            <div className="flex justify-end gap-1.5">
                              <button type="button" onClick={() => setMarking(null)} className="btn btn-ghost btn-sm">{t('plateTemplates.cancel')}</button>
                              <button type="button" onClick={mark} disabled={!marking.label.trim() || busy === `mark-${r.plateId}`} className="btn btn-primary btn-sm flex items-center gap-1">
                                {busy === `mark-${r.plateId}` ? <FiLoader size={13} className="animate-spin" /> : <FiStar size={13} />} {t('platePrompts.addFavorite')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button type="button" onClick={() => onUse(r.text)} className="btn btn-primary btn-sm">{t('platePrompts.use')}</button>
                            {!fav && (
                              <button type="button" onClick={() => setMarking({ plateId: r.plateId, label: label || '' })} className="btn btn-outline btn-sm flex items-center gap-1">
                                <FiStar size={13} /> {t('platePrompts.favorite')}
                              </button>
                            )}
                          </>
                        )}
                      </PromptCard>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * In a plate's viewer: keep the plate's prompt as a favourite, with a label —
 * or, once it is one, change its label or remove it.
 */
export const FavoriteButton = ({ plateId, onOpenLibrary }) => {
  const t = useT();
  const { data, error, loading, reload } = usePlatePrompts(true);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const [done, setDone] = useState('');
  const box = React.useRef(null);
  const favorite = data?.favorites.find((f) => f.plateId === plateId) || null;
  useEffect(() => { setOpen(false); setDone(''); }, [plateId]);
  useEffect(() => { if (open) { setLabel(favorite?.label || ''); setFailed(''); } }, [open, favorite]);
  // A click elsewhere closes it.
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  const run = async (fn, said) => {
    setBusy(true);
    setFailed('');
    try {
      await fn();
      await reload();
      setDone(said);
    } catch (e) {
      setFailed(e?.response?.data?.message || e.message || 'error');
    } finally {
      setBusy(false);
    }
  };
  const save = () => label.trim() && run(
    () => (favorite ? api.relabelPlatePromptFavorite(favorite.id, label) : api.addPlatePromptFavorite(plateId, label)),
    t('platePrompts.savedUnder', { label: label.trim() }),
  );
  const remove = () => run(() => api.removePlatePromptFavorite(favorite.id), t('platePrompts.removed'));

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setDone(''); }}
        aria-expanded={open}
        title={favorite ? t('platePrompts.isFavorite', { label: favorite.label }) : t('platePrompts.favoriteHint')}
        className={`btn btn-sm flex items-center gap-1 ${favorite ? 'btn-outline border-amber-400 text-amber-700' : 'btn-outline'}`}
      >
        <FiStar size={15} className={favorite ? 'fill-current text-amber-500' : ''} /> {favorite ? favorite.label : t('platePrompts.favorite')}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-xl p-3 space-y-2 text-left" role="dialog" aria-label={t('platePrompts.favorite')}>
          <p className="text-sm font-semibold text-gray-900">{favorite ? t('platePrompts.editFavorite') : t('platePrompts.addFavoriteTitle')}</p>
          {!data && loading && <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><FiLoader size={12} className="animate-spin" /> {t('platePrompts.loading')}</p>}
          {!data && error && <LoadError error={error} onRetry={reload} />}
          {done && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1"><FiCheck size={12} /> {done}</span>
              {onOpenLibrary && <button type="button" onClick={() => { setOpen(false); onOpenLibrary(); }} className="underline whitespace-nowrap">{t('platePrompts.seeFavorites')}</button>}
            </div>
          )}
          {failed && <p className="text-xs text-red-600">{failed}</p>}
          {data && (
          <>
          <p className="text-[11px] text-gray-500">{t('platePrompts.labelHint')}</p>
          <LabelInput value={label} onChange={setLabel} labels={data.labels} autoFocus onEnter={save} />
          <div className="flex items-center justify-between gap-2 pt-1">
            {favorite ? (
              <button type="button" onClick={remove} disabled={busy} className="btn btn-ghost btn-sm text-red-600 flex items-center gap-1"><FiTrash2 size={13} /> {t('platePrompts.remove')}</button>
            ) : <span />}
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost btn-sm">{t('plateTemplates.cancel')}</button>
              <button type="button" onClick={save} disabled={busy || !label.trim()} className="btn btn-primary btn-sm flex items-center gap-1">
                {busy ? <FiLoader size={13} className="animate-spin" /> : <FiStar size={13} />} {favorite ? t('platePrompts.saveLabel') : t('platePrompts.addFavorite')}
              </button>
            </div>
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
};
